import * as pc from 'playcanvas';
import { pickDiag } from './pickDiagnostics';
import { pickIndexCache } from './pickIndexCache';

/**
 * ExactSplatRaycaster — universal CPU splat-accurate picking.
 *
 * NOT the banned occupancy grid: this evaluates the ACTUAL gaussians
 * (center, anisotropic scale, rotation, opacity) along the pick ray — the
 * renderer's own math on the CPU — and finds the depth where accumulated
 * front-to-back opacity crosses a threshold. That is the visual surface, by
 * construction, on any scene with resident splat data. No GPU touch ever
 * (TDR-impossible), no collision dependency, editor and viewer identical.
 *
 * Coverage (all supported — no format/platform drift):
 *   - Uncompressed PLY (GSplatData) + compressed PLY (GSplatCompressedData):
 *     engine iterators over CPU-resident data.
 *   - SOG (GSplatSogData): scale/color live in GPU-only textures; residentFromSog
 *     reads back only the 5 non-SH textures and drives the iterator once.
 *   - Octree/LOD: per-splat data lives on the resident chunk resources; the octree
 *     source (OctreeSplatSource) builds a merged BVH over the currently-rendered
 *     chunks and rebuilds when the resident set changes. Picking follows the
 *     resident/rendered set at FILE granularity: a resident file is indexed whole.
 *     This can over-include splats owned by co-resident nodes drawn at a different
 *     LOD or frustum-culled — usually the same surface at finer detail (hit point
 *     nearly right); a hit on fully-occluded/culled geometry is possible but rare
 *     (front-to-back accumulation stops at the first surface). Exact per-node drawn
 *     intervals live on the renderer's GSplatOctreeInstance (filePlacements), which
 *     is not robustly reachable from the entity — file granularity is the robust
 *     choice; replicating the engine's per-node LOD selection would be fragile.
 *
 * Cost model: one chunked BVH build after load (time-sliced, ~8ms slices —
 * no jank), ~4 bytes/splat for the radius cache + ~32 bytes/BVH node; picks
 * are a BVH ray query + a few hundred gaussian evaluations (< 1–2 ms).
 *
 * Format gotcha (verified in engine 2.20): the UNCOMPRESSED iterator returns
 * LINEAR scales and SIGMOIDED alpha; the COMPRESSED iterator returns LOG
 * scales and linear alpha. Handled per-reader below — never mix.
 */

const LEAF_SIZE = 64;
const SIGMA_CUTOFF = 3;          // candidate reject beyond 3σ
const ALPHA_MIN = 0.02;          // ignore contributions fainter than this
const SURFACE_ALPHA = 0.5;       // accumulated opacity crossing = surface
const BUILD_SLICE_MS = 8;        // time-slice budget per build chunk
const MIN_PICK_T = 0.05;         // ignore hits closer than 5cm to the ray origin
const OCTREE_CHUNK_CACHE_CAP = 512; // backstop; real bound is eviction of non-resident chunks
const OCTREE_REBUILD_DEBOUNCE_MS = 300; // rendered set must be stable this long before a rebuild
const OCTREE_CHUNK_FAIL_LIMIT = 2;  // stop re-extracting a chunk after this many failures

// ── Haze-aware picks (PickOptions.seeThroughHaze — measurements) ──
// Scans of open spaces carry "haze": large, partly transparent splats floating over the real
// surface (sky-coloured floaters over a stadium field). The plain 0.5 crossing stops in them
// wherever a few overlap, although a viewer sees the field through them. A haze pick lets these
// splats through, both when crossing 0.5 and in front of what lies behind:
// - a splat is haze when it is HAZE_SIZE_FACTOR times the scene's median splat size or more and
//   less than HAZE_MAX_OPACITY opaque (an opaque splat is solid however large: floors and walls
//   in interiors are made of those);
// - a large splat among a surface's own small splats is part of that surface (a low-detail patch of
//   a field or wall): along the ray, splats closer than HAZE_RUN_GAP_SIGMAS median sizes form a run,
//   and a run whose small splats alone reach HAZE_RUN_SOLID keeps its large splats;
// - the other splats must reach SURFACE_ALPHA on their own, haze in front not counted;
// - haze that hides HAZE_OPAQUE_ALPHA of the view is itself the surface (nothing shows through);
// - a ray with only haze on it falls back to the plain crossing (sky).
// Tuned on a stadium scan's field: of 126 clicks per camera height, the plain pick landed in the
// floaters for 35 at 40°, 74 at 50° and 103 at 60° above the field; this pick for 10, 21 and 39 (the
// rest is haze opaque enough to hide the field). On two interior scans it changed no pick.
// scripts/verify-measure-picking.mjs checks it on a generated scene.
const HAZE_SIZE_FACTOR = 16;
const HAZE_MAX_OPACITY = 0.9;
const HAZE_OPAQUE_ALPHA = 0.9;
const HAZE_RUN_GAP_SIGMAS = 5;
const HAZE_RUN_SOLID = 0.1;
const HAZE_MEDIAN_SAMPLES = 32768;

type BuildCancellationCheck = () => boolean;
const BUILD_NOT_CANCELLED: BuildCancellationCheck = () => false;

class ExactSplatBuildCancelledError extends Error {
    constructor() {
        super('ExactSplatRaycaster: source disposed during index build');
        this.name = 'ExactSplatBuildCancelledError';
    }
}

function throwIfBuildCancelled(isCancelled: BuildCancellationCheck): void {
    if (isCancelled()) throw new ExactSplatBuildCancelledError();
}

function isBuildCancellation(error: unknown): boolean {
    return error instanceof ExactSplatBuildCancelledError;
}

interface SplatReader {
    /** Fill `out` with LINEAR sigma (world units, pre-entity-transform), unit quat, LINEAR alpha. */
    read(i: number, out: { sx: number; sy: number; sz: number; qx: number; qy: number; qz: number; qw: number; alpha: number }): void;
}

interface BuiltIndex {
    /** Per-node: minx,miny,minz,maxx,maxy,maxz — inflated by member 3σ radii. */
    nodeBounds: Float32Array;
    /** Per-node: [leftChild, rightChild] or [-start-1, count] for leaves. */
    nodeMeta: Int32Array;
    nodeCount: number;
    /** Splat indices reordered so leaves reference contiguous runs. */
    order: Uint32Array;
    /** Per-splat conservative radius (3σ_max), indexed by ORIGINAL splat index. */
    radius: Float32Array;
}

interface Candidate {
    t: number;
    a: number;
    /** Large and partly transparent (see HAZE_SIZE_FACTOR). */
    haze: boolean;
    /** Along-ray gap (world units) that ends a run of this splat's source (HAZE_RUN_GAP_SIGMAS). */
    gap: number;
}

/** Options for ExactSplatRaycaster.pick. */
export interface PickOptions {
    /** Measurements: look through large, partly transparent splats (haze) to the surface behind (see HAZE_SIZE_FACTOR). */
    seeThroughHaze?: boolean;
}

// Per index: the median per-splat radius (3σ of the largest axis), sampled.
const medianRadiusCache = new WeakMap<BuiltIndex, number>();

/** The median per-splat radius of an index (sampled; 0 when empty). */
function medianRadiusOf(index: Pick<BuiltIndex, 'radius'>): number {
    const cached = medianRadiusCache.get(index as BuiltIndex);
    if (cached !== undefined) return cached;
    const { radius } = index;
    const step = Math.max(1, Math.floor(radius.length / HAZE_MEDIAN_SAMPLES));
    const sample = new Float32Array(Math.ceil(radius.length / step));
    let m = 0;
    for (let i = 0; i < radius.length; i += step) sample[m++] = radius[i];
    const median = m > 0 ? sample.subarray(0, m).sort()[m >> 1] : 0;
    medianRadiusCache.set(index as BuiltIndex, median);
    return median;
}

// ── format readers ─────────────────────────────────────────────────────────

type EngineIter = { read: (i: number) => void };
type GSplatDataLike = {
    numSplats?: number;
    getProp?: (name: string) => Float32Array | undefined;
    createIter?: (p: pc.Vec3 | null, r: pc.Quat | null, s: pc.Vec3 | null, c: pc.Color | pc.Vec4 | null) => EngineIter;
    vertexData?: Uint32Array;
    chunkData?: Float32Array;
    /** GSplatSogData: scale/color live in GPU-only textures; needs decompress() before CPU reads. */
    isSog?: boolean;
    /** SOG: async GPU→CPU readback returning a resident, CPU-readable uncompressed GSplatData. */
    decompress?: () => Promise<GSplatDataLike>;
};

/** Compact, structured-clone-serializable per-splat pick data: linear scale, unit quat, [0,1] alpha. */
interface ResidentPickData {
    count: number;
    centers: Float32Array; // 3N
    sx: Float32Array; sy: Float32Array; sz: Float32Array;
    qx: Float32Array; qy: Float32Array; qz: Float32Array; qw: Float32Array;
    al: Float32Array;
}

/** Minimal shape of a PlayCanvas texture we read back to CPU (engine-internal `_levels`). */
type SogTexture = {
    width: number; height: number;
    _levels: (Uint8Array | undefined)[];
    read: (x: number, y: number, w: number, h: number, opts: { mipLevel: number; face: number; immediate: boolean }) => Promise<Uint8Array>;
};

/** GSplatSogData internals we drive directly for the minimal (no-SH) readback. Untyped in the engine. */
type SogDataLike = {
    numSplats?: number;
    meta?: { version?: number };
    prepareCodebook?: () => void;
    means_l?: SogTexture; means_u?: SogTexture; quats?: SogTexture; scales?: SogTexture; sh0?: SogTexture;
    createIter?: (p: pc.Vec3 | null, r: pc.Quat | null, s: pc.Vec3 | null, c: pc.Color | pc.Vec4 | null, sh: Float32Array | null) => EngineIter;
};

/** A resident octree chunk file resource (GSplatResource / Compressed / Sog). */
type ChunkResourceLike = {
    centers?: Float32Array;
    numSplats?: number;
    hasCenters?: boolean;
    gsplatData?: GSplatDataLike;
};

/** GSplatOctree internals used to enumerate resident chunks + their render state. */
type GSplatOctreeLike = {
    fileResources: Map<number, ChunkResourceLike>;
    fileRefCounts: Int32Array;
    files: { url: string; lodLevel: number }[];
    destroyed?: boolean;
};

function sigmoid(v: number): number { return 1 / (1 + Math.exp(-v)); }

function allocResident(n: number): ResidentPickData {
    return {
        count: n,
        centers: new Float32Array(n * 3),
        sx: new Float32Array(n), sy: new Float32Array(n), sz: new Float32Array(n),
        qx: new Float32Array(n), qy: new Float32Array(n), qz: new Float32Array(n), qw: new Float32Array(n),
        al: new Float32Array(n),
    };
}

/** A reader that indexes a compact resident block (used for SOG + cache hits). */
function readerFromResident(rd: ResidentPickData): SplatReader {
    const { sx, sy, sz, qx, qy, qz, qw, al } = rd;
    return {
        read(i, out) {
            out.sx = sx[i]; out.sy = sy[i]; out.sz = sz[i];
            out.qx = qx[i]; out.qy = qy[i]; out.qz = qz[i]; out.qw = qw[i];
            out.alpha = al[i];
        },
    };
}

/** Uncompressed PLY: engine iterator gives LINEAR scales + SIGMOIDED alpha. Exported for tests. */
export function makeUncompressedReader(data: GSplatDataLike): SplatReader | null {
    if (!data.createIter) return null;
    const r = new pc.Quat();
    const s = new pc.Vec3();
    const c = new pc.Color();
    const iter = data.createIter(null, r, s, c);
    // Some datasets store opacity already-linear; the grid's logit auto-detect
    // matters only when reading raw props — the iterator sigmoids for us.
    return {
        read(i, out) {
            iter.read(i);
            out.sx = s.x; out.sy = s.y; out.sz = s.z;           // LINEAR already
            // Raw PLY rot_* is UNNORMALIZED on disk and the iterator does not normalize —
            // the renderer normalizes right after reading (GSplatResource.updateTransformData).
            // Without this, R^T is not R^-1 and every Mahalanobis distance is distorted.
            const il = 1 / (Math.hypot(r.x, r.y, r.z, r.w) || 1);
            out.qx = r.x * il; out.qy = r.y * il; out.qz = r.z * il; out.qw = r.w * il;
            out.alpha = c.a;                                      // sigmoided already
        },
    };
}

/** Compressed PLY: engine iterator gives LOG scales + LINEAR alpha. */
function makeCompressedReader(data: GSplatDataLike): SplatReader | null {
    if (!data.createIter) return null;
    const r = new pc.Quat();
    const s = new pc.Vec3();
    // The compressed iterator writes color via unpack8888 into a Vec4 (.w = alpha).
    // A pc.Color has no .w, so passing one leaves alpha unwritten (stuck at its
    // default 1) — read from a Vec4's .w instead. (Verified in engine 2.20.)
    const c = new pc.Vec4();
    const iter = data.createIter(null, r, s, c);
    return {
        read(i, out) {
            iter.read(i);
            out.sx = Math.exp(s.x); out.sy = Math.exp(s.y); out.sz = Math.exp(s.z); // LOG → linear
            out.qx = r.x; out.qy = r.y; out.qz = r.z; out.qw = r.w;
            out.alpha = c.w;                                      // linear [0,1]
        },
    };
}

/** Raw-prop fallback for uncompressed data without a usable iterator. */
function makePropReader(data: GSplatDataLike): SplatReader | null {
    const gp = data.getProp?.bind(data);
    if (!gp) return null;
    const s0 = gp('scale_0'), s1 = gp('scale_1'), s2 = gp('scale_2');
    const r0 = gp('rot_0'), r1 = gp('rot_1'), r2 = gp('rot_2'), r3 = gp('rot_3');
    const op = gp('opacity');
    if (!s0 || !s1 || !s2 || !r0 || !r1 || !r2 || !r3) return null;
    // Logit auto-detect (same trick as the old grid): raw PLY opacity is
    // logit-space; some pipelines store it linear [0,1] already.
    let logit = false;
    if (op) {
        let mn = Infinity, mx = -Infinity;
        const step = Math.max(1, Math.floor(op.length / 4096));
        for (let i = 0; i < op.length; i += step) { const v = op[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
        logit = mn < -0.01 || mx > 1.01;
    }
    return {
        read(i, out) {
            out.sx = Math.exp(s0[i]); out.sy = Math.exp(s1[i]); out.sz = Math.exp(s2[i]);
            // rot_0 is w in PLY convention; unnormalized on disk.
            let qw = r0[i], qx = r1[i], qy = r2[i], qz = r3[i];
            const il = 1 / (Math.hypot(qx, qy, qz, qw) || 1);
            out.qx = qx * il; out.qy = qy * il; out.qz = qz * il; out.qw = qw * il;
            out.alpha = op ? (logit ? sigmoid(op[i]) : op[i]) : 1;
        },
    };
}

/**
 * FALLBACK path: build a compact resident block from a full uncompressed GSplatData
 * — the output of GSplatSogData.decompress(). Reads scale/quat/alpha (+ centers from
 * x/y/z) once into flat arrays so the heavy decompressed object (incl. unused SH
 * bands) can be GC'd. Used only when the minimal readback below can't run (unknown
 * SOG version / field shape). Centers come from the SAME data so BVH order and reader
 * index stay aligned. Time-sliced so a large scene can't block a frame.
 */
async function residentFromCpu(
    cpu: GSplatDataLike,
    isCancelled: BuildCancellationCheck = BUILD_NOT_CANCELLED,
): Promise<ResidentPickData | null> {
    throwIfBuildCancelled(isCancelled);
    const base = makeUncompressedReader(cpu) ?? makePropReader(cpu);
    const px = cpu.getProp?.('x'), py = cpu.getProp?.('y'), pz = cpu.getProp?.('z');
    if (!base || !px || !py || !pz) return null;
    const n = px.length;
    const rd = allocResident(n);
    const tmp = { sx: 0, sy: 0, sz: 0, qx: 0, qy: 0, qz: 0, qw: 1, alpha: 1 };
    let i = 0;
    while (i < n) {
        const sliceEnd = performance.now() + BUILD_SLICE_MS;
        while (i < n && performance.now() < sliceEnd) {
            const stop = Math.min(n, i + 4096);
            for (; i < stop; i++) {
                rd.centers[i * 3] = px[i]; rd.centers[i * 3 + 1] = py[i]; rd.centers[i * 3 + 2] = pz[i];
                base.read(i, tmp);
                rd.sx[i] = tmp.sx; rd.sy[i] = tmp.sy; rd.sz[i] = tmp.sz;
                rd.qx[i] = tmp.qx; rd.qy[i] = tmp.qy; rd.qz[i] = tmp.qz; rd.qw[i] = tmp.qw;
                rd.al[i] = tmp.alpha;
            }
        }
        throwIfBuildCancelled(isCancelled);
        if (i < n) {
            await new Promise((r) => setTimeout(r, 0));
            throwIfBuildCancelled(isCancelled);
        }
    }
    throwIfBuildCancelled(isCancelled);
    return rd;
}

/**
 * #1 FAST path: minimal SOG readback. Reads back ONLY the 5 non-SH textures
 * (means_l/u, quats, scales, sh0) and drives the SOG iterator once with sh=null,
 * writing scale/quat/alpha/centers straight into the compact arrays. Skips the
 * engine's full decompress(): no 45 SH bands materialised (~180 MB @ 1M splats),
 * two fewer texture readbacks, one pass instead of two. Same pick fidelity —
 * identical source textures/codebooks; SH channels never enter the pick math, and
 * it even avoids decompress()'s alpha logit round-trip. Returns null on any
 * unexpected shape (unknown version / missing field) so the caller falls back to
 * residentFromCpu(decompress()). Couples to engine-internal SOG fields — the null
 * return + fallback is the guard against a future engine layout change.
 */
async function residentFromSog(
    sog: SogDataLike,
    isCancelled: BuildCancellationCheck = BUILD_NOT_CANCELLED,
): Promise<ResidentPickData | null> {
    throwIfBuildCancelled(isCancelled);
    const n = sog.numSplats ?? 0;
    const version = sog.meta?.version;
    if (n <= 0 || typeof sog.createIter !== 'function') return null;
    if (version !== 1 && version !== 2) return null;
    const texes = [sog.means_l, sog.means_u, sog.quats, sog.scales, sog.sh0];
    for (const t of texes) {
        if (!t || typeof t.read !== 'function' || !t._levels || !t.width || !t.height) return null;
    }
    // prepareCodebook() also (re-)creates the v2 codebook GPU texture on every call,
    // orphaning the previous instance — the CPU iterator only needs the codebook-null
    // patch. Use the patch directly when the engine exposes it.
    const sogPatch = sog as { _patchCodebooks?: () => void };
    if (typeof sogPatch._patchCodebooks === 'function') sogPatch._patchCodebooks();
    else if (typeof sog.prepareCodebook === 'function') sog.prepareCodebook();
    // Snapshot each texture's original CPU level so we can restore it afterwards —
    // our readback buffers (~20 B/splat across the 5 textures) are transient scratch,
    // NOT retained for the scene's lifetime. Restoring whatever was there (ImageBitmap,
    // Uint8Array, or undefined) is a net-zero change to the texture's state and keeps
    // the memory cost identical on every platform/format. (A raw _levels[0] assignment
    // doesn't dirty the texture, so no re-upload is triggered either way.)
    const prevLevels = texes.map((t) => t!._levels[0]);
    try {
        // GPU→CPU readback of ONLY the textures the pick needs (populates _levels[0]).
        for (const t of texes) {
            throwIfBuildCancelled(isCancelled);
            const level = await t!.read(0, 0, t!.width, t!.height, { mipLevel: 0, face: 0, immediate: true });
            throwIfBuildCancelled(isCancelled);
            t!._levels[0] = level;
        }
        const p = new pc.Vec3(), r = new pc.Quat(), s = new pc.Vec3(), c = new pc.Vec4();
        const iter = sog.createIter(p, r, s, c, null); // sh=null → SH textures never dereferenced
        const rd = allocResident(n);
        let i = 0;
        while (i < n) {
            const sliceEnd = performance.now() + BUILD_SLICE_MS;
            while (i < n && performance.now() < sliceEnd) {
                const stop = Math.min(n, i + 4096);
                for (; i < stop; i++) {
                    iter.read(i);
                    rd.centers[i * 3] = p.x; rd.centers[i * 3 + 1] = p.y; rd.centers[i * 3 + 2] = p.z;
                    rd.sx[i] = Math.exp(s.x); rd.sy[i] = Math.exp(s.y); rd.sz[i] = Math.exp(s.z); // SOG scale is LOG
                    rd.qx[i] = r.x; rd.qy[i] = r.y; rd.qz[i] = r.z; rd.qw[i] = r.w;
                    rd.al[i] = c.w; // iterator already outputs final [0,1] alpha
                }
            }
            throwIfBuildCancelled(isCancelled);
            if (i < n) {
                await new Promise((res) => setTimeout(res, 0));
                throwIfBuildCancelled(isCancelled);
            }
        }
        throwIfBuildCancelled(isCancelled);
        return rd;
    } finally {
        // A chunk's textures can be destroyed (LOD stream-out) while the time-sliced loop
        // above is awaiting — destroy() nulls _levels, and a throw here would override the
        // try's completion (discarding a computed block or masking the real error).
        for (let k = 0; k < texes.length; k++) {
            try { if (texes[k]!._levels) texes[k]!._levels[0] = prevLevels[k]; } catch { /* destroyed mid-readback */ }
        }
    }
}

// ── source ─────────────────────────────────────────────────────────────────

class ExactSplatSource {
    readonly node: pc.GraphNode;
    readonly format: string;
    /** Null until an async prep (SOG readback / cache load) resolves; set synchronously for PLY. */
    centers: Float32Array | null;
    count: number;
    reader: SplatReader | null;
    index: BuiltIndex | null = null;
    buildFailed = false;
    private _buildStarted = false;
    private _failedAt = 0;
    private _retried = false;
    private _disposed = false;
    /** SOG: async producer of the compact resident block (minimal readback / decompress fallback). */
    private _prepareResident: ((isCancelled: BuildCancellationCheck) => Promise<ResidentPickData>) | null;
    /** SOG cache key (immutable asset URL); null disables the IndexedDB cache. */
    private _cacheKey: string | null;

    constructor(
        node: pc.GraphNode,
        format: string,
        ready: { centers: Float32Array; reader: SplatReader } | null,
        prepareResident: ((isCancelled: BuildCancellationCheck) => Promise<ResidentPickData>) | null,
        cacheKey: string | null,
    ) {
        this.node = node;
        this.format = format;
        this.centers = ready?.centers ?? null;
        this.reader = ready?.reader ?? null;
        this.count = this.centers ? Math.floor(this.centers.length / 3) : 0;
        this._prepareResident = prepareResident;
        this._cacheKey = cacheKey;
    }

    protected get disposed(): boolean {
        return this._disposed;
    }

    /** Cancel all asynchronous work and release references to source-owned data. */
    dispose(): void {
        this._disposed = true;
        this._prepareResident = null;
        this._cacheKey = null;
        this.centers = null;
        this.reader = null;
        this.count = 0;
        this.index = null;
    }

    /** Kick the (optionally async-prepared) time-sliced build (idempotent). */
    ensureBuild(onDone?: () => void): void {
        if (this._disposed || this._buildStarted) return;
        this._buildStarted = true;
        void this._runBuild()
            .then(() => { if (!this._disposed) onDone?.(); })
            .catch((err) => {
                if (this._disposed || isBuildCancellation(err)) return;
                this.buildFailed = true;
                this._failedAt = performance.now();
                pickDiag.push('exact:index-build-ERROR', { error: String(err) });
            });
    }

    /**
     * Permit exactly ONE build retry, >=5s after a failure — a transient GPU readback
     * hiccup during load must not disable exact picking for the whole session.
     */
    retryAfterFailure(): boolean {
        if (this._disposed || this._retried || performance.now() - this._failedAt < 5000) return false;
        this._retried = true;
        this.buildFailed = false;
        this._buildStarted = false;
        pickDiag.push('exact:index-build-retry', {});
        return true;
    }

    private async _runBuild(): Promise<void> {
        const t0 = performance.now();
        const isCancelled = (): boolean => this._disposed;
        throwIfBuildCancelled(isCancelled);
        // PLY: centers + engine-backed reader are already resident — just build the BVH.
        if (!this._prepareResident) {
            const centers = this.centers;
            const reader = this.reader;
            const count = this.count;
            if (!centers || !reader) throw new Error('ExactSplatRaycaster: source data unavailable');
            const index = await buildIndexChunked(centers, count, reader, isCancelled);
            throwIfBuildCancelled(isCancelled);
            this.index = index;
            pickDiag.push('exact:index-built', { splats: count, nodes: index.nodeCount, ms: Math.round(performance.now() - t0) });
            return;
        }
        // SOG: try the IndexedDB cache (keyed by immutable asset URL) before rebuilding.
        const cacheKey = this._cacheKey;
        if (cacheKey) {
            const hit = await pickIndexCache.get(cacheKey);
            throwIfBuildCancelled(isCancelled);
            if (hit) {
                this.centers = hit.resident.centers;
                this.reader = readerFromResident(hit.resident);
                this.count = hit.resident.count;
                this.index = hit.index;
                pickDiag.push('exact:index-cache-hit', { splats: this.count, ms: Math.round(performance.now() - t0) });
                return;
            }
        }
        const prepareResident = this._prepareResident;
        const resident = await prepareResident(isCancelled);
        throwIfBuildCancelled(isCancelled);
        const reader = readerFromResident(resident);
        const index = await buildIndexChunked(resident.centers, resident.count, reader, isCancelled);
        throwIfBuildCancelled(isCancelled);
        // Publish all source data only after the complete build still owns this source.
        this.centers = resident.centers;
        this.reader = reader;
        this.count = resident.count;
        this.index = index;
        pickDiag.push('exact:index-built', { splats: resident.count, nodes: index.nodeCount, ms: Math.round(performance.now() - t0), cached: false });
        if (cacheKey) {
            throwIfBuildCancelled(isCancelled);
            // Node arrays are allocated at maxNodes — trim to used size before
            // serializing (saves ~1 B/splat per record).
            void pickIndexCache.put(cacheKey, {
                resident,
                index: {
                    nodeBounds: index.nodeBounds.slice(0, index.nodeCount * 6),
                    nodeMeta: index.nodeMeta.slice(0, index.nodeCount * 2),
                    nodeCount: index.nodeCount, order: index.order, radius: index.radius,
                },
            });
        }
    }
}

// ── octree / LOD (Phase 2) ───────────────────────────────────────────────────

/**
 * Fingerprint the octree's currently-RENDERED file set: files both ref-counted by
 * a node's current LOD (refCount>0) AND actually resident. Folds the indices AND the
 * count — an LOD swap unloads one file and loads another (net size unchanged), so
 * size alone would miss it. Cheap: one scan of a few-hundred-entry Int32Array.
 */
export function octreeRenderedSignature(octree: GSplatOctreeLike): number {
    const rc = octree.fileRefCounts;
    let fp = 0, count = 0;
    for (let i = 0; i < rc.length; i++) {
        if (rc[i] > 0 && octree.fileResources.has(i)) { fp = (Math.imul(fp, 31) + i) >>> 0; count++; }
    }
    return (fp ^ Math.imul(count, 2654435761)) >>> 0;
}

/**
 * Per-chunk SCALE/QUAT/ALPHA only (no centers). Octree centers are always sourced
 * from the engine's CPU-resident `resource.centers` at merge time — caching them
 * here too would duplicate 12 B/splat. Retaining params-only (32 B/splat) + one
 * merged centers copy (12) + BVH (10) keeps octree at ~54 B/splat, matching SOG.
 */
interface ChunkParams {
    count: number;
    sx: Float32Array; sy: Float32Array; sz: Float32Array;
    qx: Float32Array; qy: Float32Array; qz: Float32Array; qw: Float32Array;
    al: Float32Array;
}

function allocParams(n: number): ChunkParams {
    return {
        count: n,
        sx: new Float32Array(n), sy: new Float32Array(n), sz: new Float32Array(n),
        qx: new Float32Array(n), qy: new Float32Array(n), qz: new Float32Array(n), qw: new Float32Array(n),
        al: new Float32Array(n),
    };
}

/** Extract one resident chunk's scale/quat/alpha (positions come from resource.centers). */
async function paramsFromChunk(
    resource: ChunkResourceLike,
    isCancelled: BuildCancellationCheck = BUILD_NOT_CANCELLED,
): Promise<ChunkParams | null> {
    throwIfBuildCancelled(isCancelled);
    const gd = resource.gsplatData;
    if (!gd) return null;
    // SOG chunk: params are GPU-only after upload — readback via residentFromSog; keep only its params.
    if (gd.isSog) {
        const rd = await residentFromSog(gd as unknown as SogDataLike, isCancelled);
        throwIfBuildCancelled(isCancelled);
        return rd ? { count: rd.count, sx: rd.sx, sy: rd.sy, sz: rd.sz, qx: rd.qx, qy: rd.qy, qz: rd.qz, qw: rd.qw, al: rd.al } : null;
    }
    // PLY / compressed: params via the iterator (CPU-resident, no readback).
    const n = resource.numSplats ?? (resource.centers ? Math.floor(resource.centers.length / 3) : 0);
    if (n <= 0) return null;
    const isCompressed = !!(gd.vertexData && gd.chunkData);
    const reader = isCompressed ? makeCompressedReader(gd) : (makeUncompressedReader(gd) ?? makePropReader(gd));
    if (!reader) return null;
    const params = allocParams(n);
    const tmp = { sx: 0, sy: 0, sz: 0, qx: 0, qy: 0, qz: 0, qw: 1, alpha: 1 };
    let i = 0;
    while (i < n) {
        const sliceEnd = performance.now() + BUILD_SLICE_MS;
        while (i < n && performance.now() < sliceEnd) {
            const stop = Math.min(n, i + 4096);
            for (; i < stop; i++) {
                reader.read(i, tmp);
                params.sx[i] = tmp.sx; params.sy[i] = tmp.sy; params.sz[i] = tmp.sz;
                params.qx[i] = tmp.qx; params.qy[i] = tmp.qy; params.qz[i] = tmp.qz; params.qw[i] = tmp.qw;
                params.al[i] = tmp.alpha;
            }
        }
        throwIfBuildCancelled(isCancelled);
        if (i < n) {
            await new Promise((r) => setTimeout(r, 0));
            throwIfBuildCancelled(isCancelled);
        }
    }
    throwIfBuildCancelled(isCancelled);
    return params;
}

/**
 * A reader over concatenated per-chunk param blocks, mapping a global splat index
 * to its (block, local) via binary search over blockStarts — so params are NOT
 * copied into the merged block (only centers are). blockStarts[b] = global start
 * index of block b. Reads are a handful of comparisons; the radius pass is O(N)
 * and time-sliced, and pick-time only touches a few hundred candidates.
 */
export function readerFromChunkBlocks(blocks: ChunkParams[], blockStarts: Int32Array): SplatReader {
    const nb = blocks.length;
    return {
        read(i, out) {
            let lo = 0, hi = nb - 1;
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1;
                if (blockStarts[mid] <= i) lo = mid; else hi = mid - 1;
            }
            const b = blocks[lo];
            const li = i - blockStarts[lo];
            out.sx = b.sx[li]; out.sy = b.sy[li]; out.sz = b.sz[li];
            out.qx = b.qx[li]; out.qy = b.qy[li]; out.qz = b.qz[li]; out.qw = b.qw[li];
            out.alpha = b.al[li];
        },
    };
}

/**
 * Octree/LOD pick source (Phase 2). Unlike PLY/SOG the pickable set is DYNAMIC —
 * chunks stream in/out with the camera. Semantic: picking follows the currently-
 * resident/rendered files at FILE granularity (see the class-file header for the
 * over-inclusion caveat and why exact per-node intervals aren't robustly reachable).
 * We build one merged BVH over the rendered resident chunks and rebuild it when the
 * rendered set changes (per-pick fingerprint). A per-fileIndex in-memory cache
 * avoids re-extracting unchanged chunks (esp. the SOG texture readback) on an LOD
 * swap. No IndexedDB cache — a dynamic set has no stable key.
 */
class OctreeSplatSource extends ExactSplatSource {
    private _octree: GSplatOctreeLike;
    private _chunkCache = new Map<number, ChunkParams>(); // params only; centers come from resource.centers
    private _chunkFails = new Map<number, number>();      // extraction failures per fileIndex (negative cache)
    private _lastSignature: number | null = null;
    private _buildInFlight = false;
    private _pendingSig: number | null = null;
    private _pendingSince = 0;

    constructor(node: pc.Entity, octree: GSplatOctreeLike) {
        super(node, 'octree', null, null, null);
        this._octree = octree;
    }

    dispose(): void {
        super.dispose();
        this._chunkCache.clear();
        this._chunkFails.clear();
        this._pendingSig = null;
    }

    /** Octree has no one-shot build; residency drives rebuilds. Called every pick. */
    ensureBuild(): void {
        this.refresh();
    }

    private _placementGone(): boolean {
        const gs = (this.node as unknown as { gsplat?: { _placement?: unknown } }).gsplat;
        return this.disposed || this._octree.destroyed === true || (gs != null && gs._placement == null);
    }

    /** Cheap per-pick residency check; kicks a merged rebuild when the rendered set changed. */
    refresh(): void {
        if (this._buildInFlight || this._placementGone()) return;
        const sig = octreeRenderedSignature(this._octree);
        if (sig === this._lastSignature && this.index) return;
        // Debounce (except the very first build): during continuous motion residency
        // changes faster than a rebuild completes — rebuilding on every delta would pin
        // the main thread indefinitely while picks run against a perpetually stale
        // index. Wait for the rendered set to hold still; the prior index keeps
        // serving picks in the meantime.
        if (this.index) {
            const now = performance.now();
            if (sig !== this._pendingSig) { this._pendingSig = sig; this._pendingSince = now; return; }
            if (now - this._pendingSince < OCTREE_REBUILD_DEBOUNCE_MS) return;
        }
        this._buildInFlight = true;
        void this._rebuildLoop(sig);
    }

    private async _rebuildLoop(sig: number): Promise<void> {
        try {
            // ONE build per invocation — if the set changed mid-build, the next pick's
            // refresh() re-debounces instead of chaining rebuilds back-to-back.
            await this._buildOnce(sig);
        } catch (err) {
            if (this.disposed || isBuildCancellation(err)) return;
            pickDiag.push('exact:octree-rebuild-ERROR', { error: String(err) });
        } finally {
            this._buildInFlight = false;
        }
    }

    private async _buildOnce(sig: number): Promise<void> {
        const octree = this._octree;
        const t0 = performance.now();
        const isCancelled = (): boolean => this.disposed || octree.destroyed === true;
        throwIfBuildCancelled(isCancelled);
        // 1. rendered set = ref-counted AND resident, in fileIndex order.
        const rendered: number[] = [];
        const rc = octree.fileRefCounts;
        for (let fi = 0; fi < rc.length; fi++) {
            if (rc[fi] > 0 && octree.fileResources.has(fi)) rendered.push(fi);
        }
        // 2. extract PARAMS for newly-rendered chunks into the cache (delta only). Centers
        //    are NOT cached — they come straight from the engine's resident resource.centers.
        let readbacks = 0;
        for (const fi of rendered) {
            throwIfBuildCancelled(isCancelled);
            if (this._chunkCache.has(fi)) continue;
            // Negative cache: a deterministically-failing chunk must not re-pay its
            // full extraction (up to 5 GPU readbacks for SOG) on every rebuild.
            if ((this._chunkFails.get(fi) ?? 0) >= OCTREE_CHUNK_FAIL_LIMIT) continue;
            const resource = octree.fileResources.get(fi);
            if (!resource) continue;
            try {
                const params = await paramsFromChunk(resource, isCancelled);
                throwIfBuildCancelled(isCancelled);
                if (params) { this._chunkCache.set(fi, params); this._chunkFails.delete(fi); readbacks++; }
                else this._chunkFails.set(fi, (this._chunkFails.get(fi) ?? 0) + 1);
            } catch (err) {
                if (isBuildCancellation(err)) throw err;
                throwIfBuildCancelled(isCancelled);
                const fails = (this._chunkFails.get(fi) ?? 0) + 1;
                this._chunkFails.set(fi, fails);
                pickDiag.push('exact:octree-chunk-ERROR', { fileIndex: fi, error: String(err), fails });
            }
        }
        throwIfBuildCancelled(isCancelled);
        // 3. evict cache entries no longer resident + backstop cap.
        for (const fi of [...this._chunkCache.keys()]) {
            if (!octree.fileResources.has(fi)) this._chunkCache.delete(fi);
        }
        for (const fi of [...this._chunkFails.keys()]) {
            if (!octree.fileResources.has(fi)) this._chunkFails.delete(fi); // retry when it streams back in
        }
        if (this._chunkCache.size > OCTREE_CHUNK_CACHE_CAP) {
            // Never evict a chunk in the CURRENT rendered set — that would silently
            // drop its splats from picking and re-pay its extraction next rebuild.
            const renderedSet = new Set(rendered);
            for (const fi of [...this._chunkCache.keys()]) {
                if (this._chunkCache.size <= OCTREE_CHUNK_CACHE_CAP) break;
                if (!renderedSet.has(fi)) this._chunkCache.delete(fi);
            }
            if (this._chunkCache.size > OCTREE_CHUNK_CACHE_CAP) {
                pickDiag.push('exact:octree-cache-over-cap', { size: this._chunkCache.size, rendered: rendered.length });
            }
        }
        // 4. build merged CENTERS (copied from each resident resource.centers) + param block refs.
        const blocks: ChunkParams[] = [];
        const chunkCenters: Float32Array[] = [];
        let total = 0;
        for (const fi of rendered) {
            throwIfBuildCancelled(isCancelled);
            const params = this._chunkCache.get(fi);
            const centers = octree.fileResources.get(fi)?.centers;
            // Skip a chunk whose params/centers are missing or length-mismatched (defensive).
            if (!params || params.count <= 0 || !centers || centers.length < params.count * 3) continue;
            blocks.push(params);
            chunkCenters.push(centers);
            total += params.count;
        }
        if (total === 0) {
            // Transient empty (nothing rendered yet). Keep any valid prior index and
            // never mark buildFailed — emptiness is not a failure for octree.
            throwIfBuildCancelled(isCancelled);
            this._lastSignature = sig;
            this._pendingSig = sig;
            pickDiag.push('exact:octree-empty', {});
            return;
        }
        const mergedCenters = new Float32Array(total * 3);
        const blockStarts = new Int32Array(blocks.length);
        let off = 0;
        for (let b = 0; b < blocks.length; b++) {
            throwIfBuildCancelled(isCancelled);
            blockStarts[b] = off;
            mergedCenters.set(chunkCenters[b].subarray(0, blocks[b].count * 3), off * 3);
            off += blocks[b].count;
        }
        const reader = readerFromChunkBlocks(blocks, blockStarts);
        const index = await buildIndexChunked(mergedCenters, total, reader, isCancelled);
        throwIfBuildCancelled(isCancelled);
        // 5. atomic swap — the previous index stayed pickable until this instant.
        this.centers = mergedCenters;
        this.reader = reader;
        this.count = total;
        this.index = index;
        this._lastSignature = sig;
        this._pendingSig = sig;
        pickDiag.push('exact:octree-rebuild', {
            rendered: rendered.length, cachedChunks: this._chunkCache.size,
            splats: total, readbacks, ms: Math.round(performance.now() - t0),
        });
    }
}

// ── time-sliced BVH build ──────────────────────────────────────────────────

/**
 * In-place Hoare quickselect: reorder order[start..end) by the `axis` coordinate
 * of each splat center so that order[mid] is the correct order statistic and every
 * entry left of mid is <= it and every entry right is >= it. Median-split BVH needs
 * exactly this partition — not a full sort — so it's O(n) average per node (vs the
 * old Array.from(subarray).sort() at O(n log n) + a boxed JS array per node) with
 * zero allocation. Pick results are byte-identical: node bounds stay member-inflated
 * (conservative), so leaf membership sets — hence the candidate set — are unchanged.
 */
export function selectMedianByAxis(order: Uint32Array, start: number, end: number, mid: number, centers: Float32Array, axis: number): void {
    let lo = start, hi = end - 1;
    while (lo < hi) {
        const pivot = centers[order[(lo + hi) >> 1] * 3 + axis];
        let i = lo, j = hi;
        while (i <= j) {
            while (centers[order[i] * 3 + axis] < pivot) i++;
            while (centers[order[j] * 3 + axis] > pivot) j--;
            if (i <= j) {
                const t = order[i]; order[i] = order[j]; order[j] = t;
                i++; j--;
            }
        }
        if (mid <= j) hi = j;
        else if (mid >= i) lo = i;
        else break;
    }
}

/** @internal Exported for focused source-cancellation regression tests. */
export async function buildIndexChunked(
    centers: Float32Array,
    count: number,
    reader: SplatReader,
    isCancelled: BuildCancellationCheck = BUILD_NOT_CANCELLED,
): Promise<BuiltIndex> {
    throwIfBuildCancelled(isCancelled);
    const radius = new Float32Array(count);
    const scratch = { sx: 0, sy: 0, sz: 0, qx: 0, qy: 0, qz: 0, qw: 1, alpha: 1 };

    // Pass 1: per-splat conservative radius (3 × max axis sigma), time-sliced.
    let i = 0;
    while (i < count) {
        const sliceEnd = performance.now() + BUILD_SLICE_MS;
        while (i < count && performance.now() < sliceEnd) {
            const stop = Math.min(count, i + 4096);
            for (; i < stop; i++) {
                reader.read(i, scratch);
                const m = Math.max(scratch.sx, scratch.sy, scratch.sz);
                // Guard bad data (NaN / absurd scales) — clamp to something sane.
                radius[i] = (isFinite(m) && m > 0) ? Math.min(m * SIGMA_CUTOFF, 1e4) : 0.01;
            }
        }
        throwIfBuildCancelled(isCancelled);
        if (i < count) {
            await new Promise((r) => setTimeout(r, 0));
            throwIfBuildCancelled(isCancelled);
        }
    }
    throwIfBuildCancelled(isCancelled);

    // Pass 2: median-split BVH over centers, leaves ≤ LEAF_SIZE, time-sliced.
    const order = new Uint32Array(count);
    for (let k = 0; k < count; k++) order[k] = k;
    const maxNodes = Math.max(1, Math.ceil((count / LEAF_SIZE) * 4));
    const nodeBounds = new Float32Array(maxNodes * 6);
    const nodeMeta = new Int32Array(maxNodes * 2);
    let nodeCount = 0;

    interface WorkItem { nodeIndex: number; start: number; end: number }
    const stack: WorkItem[] = [];
    const rootIndex = nodeCount++;
    stack.push({ nodeIndex: rootIndex, start: 0, end: count });

    let deadline = performance.now() + BUILD_SLICE_MS;
    while (stack.length > 0) {
        throwIfBuildCancelled(isCancelled);
        if (performance.now() > deadline) {
            await new Promise((r) => setTimeout(r, 0));
            throwIfBuildCancelled(isCancelled);
            deadline = performance.now() + BUILD_SLICE_MS;
        }
        const { nodeIndex, start, end } = stack.pop()!;
        // Bounds inflated by member radii.
        let minx = Infinity, miny = Infinity, minz = Infinity;
        let maxx = -Infinity, maxy = -Infinity, maxz = -Infinity;
        for (let k = start; k < end; k++) {
            const si = order[k];
            const r = radius[si];
            const x = centers[si * 3], y = centers[si * 3 + 1], z = centers[si * 3 + 2];
            if (x - r < minx) minx = x - r; if (x + r > maxx) maxx = x + r;
            if (y - r < miny) miny = y - r; if (y + r > maxy) maxy = y + r;
            if (z - r < minz) minz = z - r; if (z + r > maxz) maxz = z + r;
        }
        const b = nodeIndex * 6;
        nodeBounds[b] = minx; nodeBounds[b + 1] = miny; nodeBounds[b + 2] = minz;
        nodeBounds[b + 3] = maxx; nodeBounds[b + 4] = maxy; nodeBounds[b + 5] = maxz;

        const n = end - start;
        if (n <= LEAF_SIZE) {
            nodeMeta[nodeIndex * 2] = -start - 1;   // leaf marker
            nodeMeta[nodeIndex * 2 + 1] = n;
            continue;
        }
        // Split on the longest axis at the median. In-place quickselect around the
        // median position — no per-node allocation, O(n) avg vs the old full sort.
        const ex = maxx - minx, ey = maxy - miny, ez = maxz - minz;
        const axis = ex > ey ? (ex > ez ? 0 : 2) : (ey > ez ? 1 : 2);
        const mid = start + (n >> 1);
        selectMedianByAxis(order, start, end, mid, centers, axis);
        const left = nodeCount++;
        const right = nodeCount++;
        if (right >= maxNodes) throw new Error('ExactSplatRaycaster: BVH node budget exceeded');
        nodeMeta[nodeIndex * 2] = left;
        nodeMeta[nodeIndex * 2 + 1] = right;
        stack.push({ nodeIndex: left, start, end: mid });
        stack.push({ nodeIndex: right, start: mid, end });
    }

    throwIfBuildCancelled(isCancelled);
    return { nodeBounds, nodeMeta, nodeCount, order, radius };
}

// ── the raycaster ──────────────────────────────────────────────────────────

const _invMat = new pc.Mat4();
const _oLocal = new pc.Vec3();
const _dLocalP = new pc.Vec3();
const _tmpV = new pc.Vec3();
const _rotM = new pc.Mat3();
const _q = new pc.Quat();

export class ExactSplatRaycaster {
    private _sources = new Map<pc.GraphNode, ExactSplatSource>();

    /**
     * Register (or refresh) the pick source for a splat entity. Safe to call
     * every pick — it no-ops when the entity's resource is unchanged. Returns
     * true when a source exists and its index is ready.
     */
    ensureSource(entity: pc.Entity | null | undefined, cacheKey?: string | null): boolean {
        if (!entity) return false;
        const existing = this._sources.get(entity);
        if (existing) {
            if (existing.buildFailed && !existing.retryAfterFailure()) return false;
            existing.ensureBuild();
            return !!existing.index;
        }
        // blob:/file:/data: URLs are session-scoped — caching under them can never
        // hit again and only bloats the LRU (Tauri LOCAL_MODE, object-URL loads).
        const stableKey = cacheKey && /^https?:/i.test(cacheKey) ? cacheKey : null;
        const source = this._createSource(entity, stableKey);
        if (!source) return false;
        this._sources.set(entity, source);
        // The engine never sets a _destroyed flag; 'destroy' is the event GraphNode
        // actually fires — without this, a splat Replace would leave a ghost source
        // picking against freed data for the rest of the session.
        (entity as unknown as { once?: (ev: string, fn: () => void) => void }).once?.('destroy', () => this.removeSource(entity));
        source.ensureBuild();
        return !!source.index;
    }

    /**
     * True once a pick source is registered for this entity — the (async,
     * time-sliced) index build then proceeds on its own. Lets a caller prime the
     * index at scene-load time and stop retrying as soon as the gsplat resource is
     * resident, rather than paying the build on the first pick. Distinct from
     * ensureSource's return, which is false until the index has finished building.
     */
    hasSource(entity: pc.Entity | null | undefined): boolean {
        if (!entity) return false;
        const s = this._sources.get(entity);
        return !!s && !s.buildFailed;
    }

    /** Drop a source (entity destroyed / splat swapped). */
    removeSource(entity: pc.GraphNode): void {
        this._sources.get(entity)?.dispose();
        this._sources.delete(entity);
    }

    private _createSource(entity: pc.Entity, cacheKey: string | null): ExactSplatSource | null {
        // Same resolution chain the previous pickers used — resource may hang
        // off several component internals depending on engine path.
        const gs = entity.gsplat as unknown as {
            instance?: { resource?: unknown }; _instance?: { resource?: unknown };
            _placement?: { resource?: unknown }; resource?: unknown; asset?: { resource?: unknown };
        } | undefined;
        const resource = (gs?.instance?.resource ?? gs?._instance?.resource ?? gs?._placement?.resource ?? gs?.resource ?? gs?.asset?.resource) as {
            centers?: Float32Array;
            gsplatData?: GSplatDataLike;
            octree?: unknown;
        } | undefined;
        if (!resource) { pickDiag.push('exact:no-resource', { entity: entity.name }); return null; }
        // Octree/LOD (Phase 2): per-splat data lives on the resident CHUNK resources
        // inside resource.octree.fileResources, not on the octree resource itself.
        if (resource.octree) {
            const oc = resource.octree as {
                fileResources?: unknown; fileRefCounts?: { length?: number }; files?: unknown; destroyed?: boolean;
            };
            const shapeOk = oc.fileResources instanceof Map
                && oc.fileRefCounts != null && typeof oc.fileRefCounts.length === 'number'
                && Array.isArray(oc.files);
            if (!shapeOk) { pickDiag.push('exact:octree-shape-unsupported', { entity: entity.name }); return null; }
            const octree = resource.octree as unknown as GSplatOctreeLike;
            pickDiag.push('exact:source-added', { entity: entity.name, format: 'octree', files: octree.files.length });
            return new OctreeSplatSource(entity, octree);
        }
        const centers = resource.centers;
        const data = resource.gsplatData;
        if (!centers || !data) { pickDiag.push('exact:no-centers-or-data', { entity: entity.name, centers: !!centers, data: !!data }); return null; }

        // SOG (GSplatSogData): scale/color live in GPU-only texture levels that the CPU
        // iterator reads as `undefined` → NaN for every splat (its vertexData/chunkData
        // are absent, so it also fails the compressed check below). Read the params to CPU
        // — fast path (#1) reads only the 5 non-SH textures; fallback decompresses fully —
        // and build from that. Async: the source becomes ready on a later pick; until then
        // the caller falls back to the collision raycast. Result is cacheable in IndexedDB
        // keyed by the immutable asset URL (SOG only).
        if (data.isSog && data.decompress) {
            const sog = data as unknown as SogDataLike;
            const decompress = data.decompress.bind(data);
            pickDiag.push('exact:source-added', { entity: entity.name, splats: Math.floor(centers.length / 3), format: 'sog' });
            const prepareResident = async (isCancelled: BuildCancellationCheck): Promise<ResidentPickData> => {
                throwIfBuildCancelled(isCancelled);
                try {
                    const fast = await residentFromSog(sog, isCancelled);
                    throwIfBuildCancelled(isCancelled);
                    if (fast) { pickDiag.push('exact:sog-readback', { splats: fast.count, mode: 'minimal' }); return fast; }
                } catch (err) {
                    if (isBuildCancellation(err)) throw err;
                    throwIfBuildCancelled(isCancelled);
                    pickDiag.push('exact:sog-minimal-fallback', { error: String(err) });
                }
                // The engine decompress() fallback materializes up to ~236 B/splat of SH
                // bands fully synchronously — OOM territory on big scenes / small devices.
                // Above a device-aware budget, fail the build instead (the collision
                // fallback keeps picking usable).
                const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
                const capSplats = mem !== undefined && mem <= 4 ? 500_000 : 2_000_000;
                const nSplats = Math.floor(centers.length / 3);
                if (nSplats > capSplats) {
                    throw new Error(`SOG decompress fallback skipped: ${nSplats} splats exceeds device budget ${capSplats}`);
                }
                throwIfBuildCancelled(isCancelled);
                const cpu = await decompress();
                throwIfBuildCancelled(isCancelled);
                const rd = await residentFromCpu(cpu, isCancelled);
                throwIfBuildCancelled(isCancelled);
                if (!rd) throw new Error('SOG decompress produced no usable reader');
                pickDiag.push('exact:sog-readback', { splats: rd.count, mode: 'decompress' });
                return rd;
            };
            return new ExactSplatSource(entity, 'sog', null, prepareResident, cacheKey);
        }

        // Compressed data carries vertexData/chunkData; uncompressed carries getProp.
        const isCompressed = !!(data.vertexData && data.chunkData);
        let reader: SplatReader | null;
        let format: string;
        if (isCompressed) {
            reader = makeCompressedReader(data);
            format = 'compressed-ply';
        } else {
            reader = makeUncompressedReader(data);
            format = 'ply-iter';
            if (!reader) { reader = makePropReader(data); format = 'ply-prop'; }
        }
        if (!reader) { pickDiag.push('exact:no-reader', { entity: entity.name, compressed: isCompressed }); return null; }
        pickDiag.push('exact:source-added', { entity: entity.name, splats: Math.floor(centers.length / 3), format });
        // PLY reads from engine-resident data each pick (no decompress) — not cached.
        return new ExactSplatSource(entity, format, { centers, reader }, null, null);
    }

    /**
     * Pick the splat surface along a world-space ray. Returns null when no
     * source is ready or accumulated opacity never crosses the threshold —
     * callers fall back to the collision raycast.
     */
    pick(
        originWorld: pc.Vec3,
        dirWorldNormalized: pc.Vec3,
        maxDist: number,
        /** Restrict the query to one rendered splat (used by split compare). */
        onlyEntity?: pc.Entity | null,
        options?: PickOptions,
    ): pc.Vec3 | null {
        if (this._sources.size === 0) return null;
        const t0 = performance.now();
        const candidates: Candidate[] = [];
        let evaluated = 0;

        for (const source of this._sources.values()) {
            if (onlyEntity && source.node !== onlyEntity) continue;
            // Self-prune belt-and-braces (the 'destroy' listener registered in
            // ensureSource is the real cleanup — the engine never sets _destroyed).
            const ent = source.node as pc.Entity & { _destroyed?: boolean };
            if (ent._destroyed) {
                this.removeSource(source.node);
                continue;
            }
            // Swap/staging flows disable the outgoing entity while keeping it alive —
            // a hidden splat must not catch picks.
            if (ent.enabled === false) continue;
            const index = source.index;
            if (!index) continue;

            // World → source-local (handles entity translate/rotate/scale).
            const world = source.node.getWorldTransform();
            _invMat.copy(world).invert();
            _invMat.transformPoint(originWorld, _oLocal);
            _tmpV.copy(originWorld).add(dirWorldNormalized);
            _invMat.transformPoint(_tmpV, _dLocalP);
            const dLx = _dLocalP.x - _oLocal.x, dLy = _dLocalP.y - _oLocal.y, dLz = _dLocalP.z - _oLocal.z;
            const localPerWorld = Math.hypot(dLx, dLy, dLz) || 1; // local units per world unit along ray
            const il = 1 / localPerWorld;
            const dx = dLx * il, dy = dLy * il, dz = dLz * il;    // unit local dir
            const maxDistLocal = maxDist * localPerWorld;
            // MIN_PICK_T is a WORLD-space 5cm; convert per source so the near-camera
            // dead zone doesn't scale with entity/scene scale.
            const minPickTLocal = MIN_PICK_T * localPerWorld;

            evaluated += this._gather(source, index, _oLocal.x, _oLocal.y, _oLocal.z, dx, dy, dz, maxDistLocal, minPickTLocal, localPerWorld, candidates);
        }

        if (candidates.length === 0) {
            pickDiag.push('exact:pick-miss', { evaluated, ms: Math.round((performance.now() - t0) * 10) / 10 });
            return null;
        }

        candidates.sort((a, b) => a.t - b.t);
        if (options?.seeThroughHaze) return this._pickThroughHaze(candidates, originWorld, dirWorldNormalized, evaluated, t0);
        let transmittance = 1;
        let accumulated = 0;
        for (const cnd of candidates) {
            // Last-line guard: one degenerate splat (NaN alpha/scale/quat/center) must
            // never poison the composite. NaN propagates through `accumulated`, and since
            // every `>=`/`<` comparison against NaN is false, it turns a real surface hit
            // into a permanent "translucent-miss". _gather already drops non-finite
            // contributions; this belt-and-suspenders keeps the accumulation site safe.
            if (!Number.isFinite(cnd.a)) continue;
            accumulated += transmittance * cnd.a;
            transmittance *= (1 - cnd.a);
            if (accumulated >= SURFACE_ALPHA) {
                const point = new pc.Vec3(
                    originWorld.x + dirWorldNormalized.x * cnd.t,
                    originWorld.y + dirWorldNormalized.y * cnd.t,
                    originWorld.z + dirWorldNormalized.z * cnd.t,
                );
                pickDiag.push('exact:pick-hit', {
                    t: Math.round(cnd.t * 1000) / 1000,
                    candidates: candidates.length,
                    evaluated,
                    ms: Math.round((performance.now() - t0) * 10) / 10,
                });
                return point;
            }
        }
        pickDiag.push('exact:pick-translucent-miss', {
            candidates: candidates.length,
            accumulated: Math.round(accumulated * 100) / 100,
            ms: Math.round((performance.now() - t0) * 10) / 10,
        });
        return null;
    }

    /**
     * The haze-aware crossing over sorted candidates (see HAZE_SIZE_FACTOR): the first depth where
     * the non-haze splats reach SURFACE_ALPHA by themselves, or where everything together hides
     * HAZE_OPAQUE_ALPHA of the view; the plain crossing when only haze is on the ray.
     */
    private _pickThroughHaze(
        candidates: Candidate[], originWorld: pc.Vec3, dir: pc.Vec3, evaluated: number, t0: number,
    ): pc.Vec3 | null {
        // Runs: a large splat among the surface's own small splats belongs to that surface.
        let runStart = 0;
        for (let i = 1; i <= candidates.length; i++) {
            if (i < candidates.length && candidates[i].t - candidates[i - 1].t <= Math.max(candidates[i].gap, candidates[i - 1].gap)) continue;
            let runSolid = 0, runT = 1;
            for (let j = runStart; j < i; j++) {
                const c = candidates[j];
                if (!c.haze && Number.isFinite(c.a)) { runSolid += runT * c.a; runT *= (1 - c.a); }
            }
            if (runSolid >= HAZE_RUN_SOLID) for (let j = runStart; j < i; j++) candidates[j].haze = false;
            runStart = i;
        }
        let solid = 0, solidT = 1, all = 0, allT = 1, hazeSkipped = 0;
        let plain: Candidate | null = null;
        let hit: Candidate | null = null;
        for (const cnd of candidates) {
            if (!Number.isFinite(cnd.a)) continue;
            all += allT * cnd.a;
            allT *= (1 - cnd.a);
            if (!plain && all >= SURFACE_ALPHA) plain = cnd;
            if (cnd.haze) {
                hazeSkipped++;
            } else {
                solid += solidT * cnd.a;
                solidT *= (1 - cnd.a);
                if (solid >= SURFACE_ALPHA) { hit = cnd; break; }
            }
            if (all >= HAZE_OPAQUE_ALPHA) { hit = cnd; break; }
        }
        hit ??= plain;
        if (!hit) {
            pickDiag.push('exact:pick-translucent-miss', {
                candidates: candidates.length, accumulated: Math.round(all * 100) / 100, haze: true,
                ms: Math.round((performance.now() - t0) * 10) / 10,
            });
            return null;
        }
        pickDiag.push('exact:pick-hit', {
            t: Math.round(hit.t * 1000) / 1000,
            candidates: candidates.length,
            evaluated,
            hazeSkipped,
            ms: Math.round((performance.now() - t0) * 10) / 10,
        });
        return new pc.Vec3(
            originWorld.x + dir.x * hit.t,
            originWorld.y + dir.y * hit.t,
            originWorld.z + dir.z * hit.t,
        );
    }

    /** BVH traversal + exact anisotropic gaussian evaluation. Returns #evaluated. */
    private _gather(
        source: ExactSplatSource, index: BuiltIndex,
        ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number,
        maxDistLocal: number, minPickTLocal: number, localPerWorld: number,
        out: Candidate[],
    ): number {
        const { nodeBounds, nodeMeta, order, radius } = index;
        const centers = source.centers;
        const reader = source.reader;
        if (!centers || !reader) return 0; // async prep not resolved yet (defensive; index implies both set)
        const scratch = { sx: 0, sy: 0, sz: 0, qx: 0, qy: 0, qz: 0, qw: 1, alpha: 1 };
        const medianRadius = medianRadiusOf(index);
        // No sample (empty index) or a zero median: nothing is haze.
        const hazeRadius = medianRadius > 0 ? medianRadius * HAZE_SIZE_FACTOR : Infinity;
        // radius is 3σ: the run gap in σ, converted to world units along the ray.
        const runGap = (medianRadius / SIGMA_CUTOFF) * HAZE_RUN_GAP_SIGMAS / localPerWorld;
        const idx = 1 / (dx || 1e-20), idy = 1 / (dy || 1e-20), idz = 1 / (dz || 1e-20);
        const stack = [0];
        let evaluated = 0;
        let nonFinite = 0;

        while (stack.length > 0) {
            const ni = stack.pop()!;
            const b = ni * 6;
            // Slab test.
            let t1 = (nodeBounds[b] - ox) * idx, t2 = (nodeBounds[b + 3] - ox) * idx;
            let tmin = Math.min(t1, t2), tmax = Math.max(t1, t2);
            t1 = (nodeBounds[b + 1] - oy) * idy; t2 = (nodeBounds[b + 4] - oy) * idy;
            tmin = Math.max(tmin, Math.min(t1, t2)); tmax = Math.min(tmax, Math.max(t1, t2));
            t1 = (nodeBounds[b + 2] - oz) * idz; t2 = (nodeBounds[b + 5] - oz) * idz;
            tmin = Math.max(tmin, Math.min(t1, t2)); tmax = Math.min(tmax, Math.max(t1, t2));
            if (tmax < Math.max(0, tmin) || tmin > maxDistLocal) continue;

            const m0 = nodeMeta[ni * 2];
            if (m0 >= 0) { stack.push(m0, nodeMeta[ni * 2 + 1]); continue; }

            // Leaf: evaluate members.
            const start = -m0 - 1;
            const n = nodeMeta[ni * 2 + 1];
            for (let k = 0; k < n; k++) {
                const si = order[start + k];
                const cxv = centers[si * 3], cyv = centers[si * 3 + 1], czv = centers[si * 3 + 2];
                // Quick sphere reject: distance from ray to center vs 3σ_max.
                const px = cxv - ox, py = cyv - oy, pz = czv - oz;
                const tc = px * dx + py * dy + pz * dz;
                if (tc < minPickTLocal || tc > maxDistLocal) continue;
                const qx2 = px - tc * dx, qy2 = py - tc * dy, qz2 = pz - tc * dz;
                const r = radius[si];
                if (qx2 * qx2 + qy2 * qy2 + qz2 * qz2 > r * r) continue;

                // Exact anisotropic evaluation at the ray's Mahalanobis-closest point.
                reader.read(si, scratch);
                evaluated++;
                if (scratch.alpha < ALPHA_MIN) continue;
                _q.set(scratch.qx, scratch.qy, scratch.qz, scratch.qw);
                _rotM.setFromQuat(_q);
                const m = _rotM.data; // column-major 3x3, columns are gaussian axes
                const isx = 1 / Math.max(scratch.sx, 1e-6);
                const isy = 1 / Math.max(scratch.sy, 1e-6);
                const isz = 1 / Math.max(scratch.sz, 1e-6);
                // u = Σ^{-1/2} Rᵀ (o - c);  v = Σ^{-1/2} Rᵀ d
                const rox = -px, roy = -py, roz = -pz; // (o - c)
                const ux = (m[0] * rox + m[1] * roy + m[2] * roz) * isx;
                const uy = (m[3] * rox + m[4] * roy + m[5] * roz) * isy;
                const uz = (m[6] * rox + m[7] * roy + m[8] * roz) * isz;
                const vx = (m[0] * dx + m[1] * dy + m[2] * dz) * isx;
                const vy = (m[3] * dx + m[4] * dy + m[5] * dz) * isy;
                const vz = (m[6] * dx + m[7] * dy + m[8] * dz) * isz;
                const vv = vx * vx + vy * vy + vz * vz;
                if (vv < 1e-12) continue;
                const tStar = -(ux * vx + uy * vy + uz * vz) / vv;
                if (tStar < minPickTLocal || tStar > maxDistLocal) continue;
                const ex = ux + tStar * vx, ey = uy + tStar * vy, ez = uz + tStar * vz;
                const mahalanobisSq = ex * ex + ey * ey + ez * ez;
                if (mahalanobisSq > SIGMA_CUTOFF * SIGMA_CUTOFF) continue;
                const a = Math.min(0.999, scratch.alpha * Math.exp(-0.5 * mahalanobisSq));
                // Reject non-finite contributions from degenerate gaussians (NaN/zero
                // scale, unnormalized/zero quat, NaN opacity). The scale "clamp" above
                // does NOT stop this — Math.max(NaN, 1e-6) === NaN — so `a` can be NaN,
                // and `a < ALPHA_MIN` would let it through (NaN < x === false). Test
                // finiteness explicitly, before the threshold, so a bad splat is dropped
                // instead of poisoning the whole front-to-back accumulation.
                if (!Number.isFinite(a)) { nonFinite++; continue; }
                if (a < ALPHA_MIN) continue;
                out.push({ t: tStar / localPerWorld, a, haze: r > hazeRadius && scratch.alpha < HAZE_MAX_OPACITY, gap: runGap });
            }
        }
        // Surface degenerate data. If this equals the candidate count on a miss, the
        // scene's splat scales/opacities are systematically NaN (a reader/format bug),
        // not just a few pruned splats — the guards above keep picking working either way.
        if (nonFinite > 0) pickDiag.push('exact:nan-splats-skipped', { count: nonFinite });
        return evaluated;
    }
}
